import Head from 'next/head';
import { safeJsonLd } from '@c/StructuredData';
import SEO from '@c/SEO';
import { MasterDocument } from '@util/firestore/master';
import { ListingDisplay } from '@c/MasterProductsPageV2';

interface MasterSEOProps {
  master: MasterDocument;
  listings: ListingDisplay[];
}

function slugifyCategory(cat: string): string {
  return cat.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function getProductTypeLabel(productType?: string): string {
  const map: Record<string, string> = {
    mirrorless_camera: 'Mirrorless Camera',
    dslr: 'DSLR Camera',
    medium_format: 'Medium Format Camera',
    compact_camera: 'Compact Camera',
    film_camera: 'Film Camera',
    cinema_camera: 'Cinema Camera',
    camcorder: 'Camcorder',
    action_camera: 'Action Camera',
    photo_lens: 'Lens',
    cinema_lens: 'Cinema Lens',
    drone: 'Drone',
    flash: 'Flash',
    tripod: 'Tripod',
    gimbal: 'Gimbal',
    monitor: 'Monitor',
    recorder: 'Recorder',
    microphone: 'Microphone',
    lighting: 'Lighting',
    accessory: 'Accessory',
  };
  return map[productType || ''] || '';
}

/**
 * Smart noindex: only noindex truly empty pages.
 * Index when the page has meaningful content for search engines.
 */
function shouldNoIndex(master: MasterDocument, activeListingCount: number): boolean {
  const hasOverview = !!(master.gmOverview || master.description);
  const hasFeatures = !!(master.gmFeatures && master.gmFeatures.length >= 2);
  const hasSpecs = !!(
    master.specifications && Object.keys(master.specifications).length >= 3
  );
  const hasListings = activeListingCount > 0;

  // noindex when ALL content signals are missing
  if (!hasOverview && !hasFeatures && !hasSpecs && !hasListings) {
    return true;
  }

  return false;
}

export default function MasterSEO({ master, listings }: MasterSEOProps) {
  // Compute active listings (not out of stock, and verified)
  const activePrices = listings
    .filter((l) => !l.out_of_stock && l.is_verified)
    .map((l) => l.price)
    .sort((a, b) => a - b);

  const productTypeLabel = getProductTypeLabel(master.productType);

  // Description: prefer gmOverview, fall back to description, fall back to minimal string
  const descriptionSource =
    master.gmOverview
      ? master.gmOverview.substring(0, 160)
      : master.description
      ? master.description.substring(0, 160)
      : `${master.brand} ${master.model} on GearFocus`;

  // ── Schema 1: Product (no offers — /m/ pages must never appear in GMC) ──────

  const productSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: master.title,
    brand: { '@type': 'Brand', name: master.brand },
    model: master.model,
    description: descriptionSource,
    image: master.images?.[0] ?? undefined,
    category: master.categories?.join(' > ') ?? undefined,
  };

  // Only include sku/gtin/mpn when the fields exist and are non-empty
  if (master.mpn) {
    productSchema.sku = master.mpn;
    productSchema.mpn = master.mpn;
  }
  if (master.gtin) {
    productSchema.gtin = master.gtin;
  }

  // Add review array for Product schema validity (requires offers, review, or aggregateRating)
  if (master.reviews && master.reviews.length > 0) {
    const validReviews = master.reviews
      .filter((r) => r.url && r.source)
      .slice(0, 5)
      .map((r) => ({
        '@type': 'Review' as const,
        name: r.title,
        author: { '@type': 'Organization' as const, name: r.source },
        url: r.url,
      }));
    if (validReviews.length > 0) {
      productSchema.review = validReviews;
    }
  }

  // ── Schema 2: BreadcrumbList ─────────────────────────────────────────────────

  const breadcrumbItems: Array<Record<string, unknown>> = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://gearfocus.com',
    },
  ];

  (master.categories || []).forEach((cat, idx) => {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: idx + 2,
      name: cat,
      item: `https://gearfocus.com/c/${slugifyCategory(cat)}`,
    });
  });

  // Final item: the product itself
  breadcrumbItems.push({
    '@type': 'ListItem',
    position: breadcrumbItems.length + 1,
    name: master.title,
    item: `https://gearfocus.com/m/${master.slug}`,
  });

  const breadcrumbSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  };

  // ── Schema 3: FAQPage (only when gmFaq has items) ───────────────────────────

  const faqSchema: Record<string, unknown> | null =
    master.gmFaq && master.gmFaq.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: master.gmFaq.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }
      : null;

  // ── Schema 4: VideoObject (first 1–3 featured or top-viewed videos) ─────────

  const videoSchemas: Array<Record<string, unknown>> = [];

  if (master.youtube_videos && master.youtube_videos.length > 0) {
    // Featured first; if none featured, fall back to sort by views descending
    const featured = master.youtube_videos.filter((v) => v.featured === true);
    const candidates =
      featured.length > 0
        ? featured
        : [...master.youtube_videos].sort(
            (a, b) => (b.views ?? 0) - (a.views ?? 0)
          );

    candidates.slice(0, 3).forEach((video) => {
      const videoSchema: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: video.title,
        description: `${video.title} — ${master.brand} ${master.model} on GearFocus.`,
        thumbnailUrl: `https://img.youtube.com/vi/${video.videoId}/maxresdefault.jpg`,
        contentUrl: `https://www.youtube.com/watch?v=${video.videoId}`,
        embedUrl: `https://www.youtube.com/embed/${video.videoId}`,
      };

      // Normalize uploadDate to full ISO 8601 with timezone
      const rawDate = video.date;
      if (rawDate) {
        if (typeof rawDate === 'object' && 'toDate' in rawDate) {
          // Firestore Timestamp
          videoSchema.uploadDate = (rawDate as { toDate(): Date }).toDate().toISOString();
        } else if (typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(rawDate)) {
          // Bare date string (YYYY-MM-DD) → append timezone
          videoSchema.uploadDate = `${rawDate}T00:00:00Z`;
        } else {
          // Already full ISO 8601 or other format — pass through
          videoSchema.uploadDate = rawDate;
        }
      }

      videoSchemas.push(videoSchema);
    });
  }

  // ── Combine all schemas, drop nulls ─────────────────────────────────────────

  const schemas = [
    productSchema,
    breadcrumbSchema,
    faqSchema,
    ...videoSchemas,
  ].filter(Boolean);

  return (
    <>
      <SEO
        title={`${master.brand} ${master.model}${productTypeLabel ? ` ${productTypeLabel}` : ''} Used — For Sale, Specs & Reviews`}
        description={descriptionSource}
        ogImage={master.images?.[0] || 'https://gearfocus.com/gear_pile.png'}
        ogType="website"
        keywords={[
          master.brand,
          master.model,
          ...(master.categories || []),
          'used',
          'buy',
          'camera gear',
        ]}
        noIndex={shouldNoIndex(master, activePrices.length)}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: safeJsonLd(schemas) }}
        />
      </Head>
    </>
  );
}
